import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { GraphicEq, Keyboard, Mic, Replay, VolumeUp } from '@mui/icons-material';
import { normalizeSpokenNumber } from '../../utils/spokenNumber';

export interface VoiceQuizQuestion {
  id: string;
  /** Spoken caption. This is what TTS reads and what stays on screen. */
  content: string;
  correctAnswer: number;
  points?: number;
  tolerance?: number;
  options?: {
    tolerance?: number;
  };
}

export interface VoiceQuizAnswer {
  questionId: string;
  promptSpoken: string;
  expectedAnswer: number;
  answerRaw: string;
  answerNormalized: number | null;
  inputMethod: 'VOICE' | 'TYPED';
  sttConfidence?: number;
  tolerance?: number;
  correct: boolean;
}

interface VoiceQuizRunnerProps {
  questions: VoiceQuizQuestion[];
  onComplete: (answers: VoiceQuizAnswer[], score: number) => void;
  /** Pause before the next question speaks. */
  advancePauseMs?: number;
  typedDefault?: boolean;
}

const SPEECH_RATES = [0.8, 1, 1.25] as const;
const RATE_STORAGE_KEY = 'abacus-voice-rate';
const LOW_CONFIDENCE = 0.6;

type SpeechCtor = new () => SpeechRecognition;

function readStoredRate(): number {
  try {
    const saved = window.sessionStorage.getItem(RATE_STORAGE_KEY);
    const value = saved ? Number(saved) : 1;
    return SPEECH_RATES.includes(value as (typeof SPEECH_RATES)[number]) ? value : 1;
  } catch {
    return 1;
  }
}

function recognitionConstructor(): SpeechCtor | null {
  const host = window as typeof window & {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return host.SpeechRecognition || host.webkitSpeechRecognition || null;
}

/**
 * Speaks a mental-arithmetic caption and accepts a spoken or typed number.
 * A listen that is noisy, denied, or below the confidence threshold does not score.
 */
const VoiceQuizRunner: React.FC<VoiceQuizRunnerProps> = ({
  questions,
  onComplete,
  advancePauseMs = 800,
  typedDefault = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<VoiceQuizAnswer[]>([]);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [micSupported, setMicSupported] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  const [rate, setRate] = useState(readStoredRate);
  const [advancing, setAdvancing] = useState(false);
  const [showTyped, setShowTyped] = useState(typedDefault);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const advanceTimer = useRef<number | null>(null);
  const currentQuestion = questions[currentIndex];
  const showTypedPrompt = showTyped || !micSupported || micDenied;

  useEffect(() => {
    const ctor = recognitionConstructor();
    setSpeechSupported(typeof window !== 'undefined' && 'speechSynthesis' in window);
    setMicSupported(Boolean(ctor));
    if (ctor) {
      const recognition = new ctor();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      recognitionRef.current = recognition;
    }
    return () => {
      window.speechSynthesis?.cancel();
      recognitionRef.current?.stop();
      if (advanceTimer.current != null) {
        window.clearTimeout(advanceTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!currentQuestion || !speechSupported) return;
    const caption = currentQuestion.content;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(caption);
    utterance.rate = rate;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => {
      setIsSpeaking(false);
      setError('Could not play audio prompt. You can still answer by typing.');
    };
    window.speechSynthesis.speak(utterance);
    setTypedAnswer('');
    setError(null);
    setAdvancing(false);
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [currentQuestion?.id, currentQuestion?.content, speechSupported, rate, currentIndex]);

  const rememberRate = (next: number) => {
    setRate(next);
    try {
      window.sessionStorage.setItem(RATE_STORAGE_KEY, String(next));
    } catch {
      // Preference is optional when storage is blocked.
    }
  };

  const replay = () => {
    if (!currentQuestion || !speechSupported) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(currentQuestion.content);
    utterance.rate = rate;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const finishAnswer = (
    raw: string,
    normalized: number | null,
    inputMethod: 'VOICE' | 'TYPED',
    sttConfidence?: number
  ) => {
    if (!currentQuestion || advancing) return;
    const tolerance = currentQuestion.tolerance ?? currentQuestion.options?.tolerance ?? 0;
    const correct =
      normalized !== null && Math.abs(normalized - currentQuestion.correctAnswer) <= tolerance;
    const answer: VoiceQuizAnswer = {
      questionId: currentQuestion.id,
      promptSpoken: currentQuestion.content,
      expectedAnswer: currentQuestion.correctAnswer,
      answerRaw: raw,
      answerNormalized: normalized,
      inputMethod,
      sttConfidence,
      tolerance,
      correct,
    };
    const nextAnswers = [...answers, answer];
    setAnswers(nextAnswers);
    setAdvancing(true);

    if (currentIndex === questions.length - 1) {
      const score = nextAnswers.reduce((acc, item, idx) => {
        if (!item.correct) return acc;
        return acc + (questions[idx]?.points ?? 1);
      }, 0);
      onComplete(nextAnswers, score);
      return;
    }

    advanceTimer.current = window.setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
    }, advancePauseMs);
  };

  const handleListen = () => {
    const recognition = recognitionRef.current;
    if (!recognition || !currentQuestion || micDenied) {
      setError('Type your answer');
      return;
    }
    setError(null);
    setIsListening(true);

    recognition.onresult = (event) => {
      const result = event.results[0]?.[0];
      const transcript = result?.transcript ?? '';
      const confidence = result?.confidence;
      setIsListening(false);
      if (typeof confidence === 'number' && confidence > 0 && confidence < LOW_CONFIDENCE) {
        setError('Try again or type');
        return;
      }
      const normalized = normalizeSpokenNumber(transcript);
      if (normalized === null) {
        setError('Could not interpret that as a number. Try again or type your answer.');
        return;
      }
      finishAnswer(transcript, normalized, 'VOICE', confidence);
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      const code = event.error;
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        setMicDenied(true);
        setError('Type your answer');
        return;
      }
      setError("Didn't catch that. Try again or type.");
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    try {
      recognition.start();
    } catch {
      setIsListening(false);
      setError("Didn't catch that. Try again or type.");
    }
  };

  const handleTypedSubmit = () => {
    const normalized = normalizeSpokenNumber(typedAnswer);
    if (normalized === null) {
      setError('Enter a number, such as 20 or -3.');
      return;
    }
    finishAnswer(typedAnswer, normalized, 'TYPED');
  };

  if (!currentQuestion) {
    return (
      <Alert severity="warning">
        No mental arithmetic questions available for this quiz.
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, pb: 16 }} data-testid="voice-quiz-runner">
      <Stack spacing={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Mental Arithmetic
          </Typography>
          <Chip label={`${currentIndex + 1}/${questions.length}`} color="primary" />
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          data-testid="voice-quiz-progress"
          aria-label={`Question ${currentIndex + 1} of ${questions.length}`}
        >
          {questions.map((question, index) => (
            <Box
              key={question.id || index}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: index <= currentIndex ? 'primary.main' : 'grey.300',
              }}
            />
          ))}
        </Stack>

        <Alert icon={<VolumeUp fontSize="inherit" />} severity="info">
          <Typography data-testid="voice-quiz-caption" aria-live="polite" variant="h5">
            {currentQuestion.content}
          </Typography>
        </Alert>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2">Speed</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={rate}
            onChange={(_event, next: number | null) => {
              if (next != null) rememberRate(next);
            }}
            aria-label="Speaking speed"
          >
            {SPEECH_RATES.map((option) => (
              <ToggleButton key={option} value={option}>
                {option === 1 ? '1.0' : option.toFixed(2).replace(/0$/, '')}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={isSpeaking ? <CircularProgress size={16} /> : <Replay />}
            onClick={replay}
            disabled={!speechSupported || isSpeaking}
          >
            Replay prompt
          </Button>
        </Stack>

        {!showTyped && micSupported && !micDenied && (
          <Button startIcon={<Keyboard />} onClick={() => setShowTyped(true)}>
            Type instead
          </Button>
        )}

        {showTypedPrompt && (
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              fullWidth
              type="number"
              slotProps={{ htmlInput: { 'data-testid': 'voice-quiz-typed' } }}
              label="Type your answer"
              value={typedAnswer}
              onChange={(event) => setTypedAnswer(event.target.value)}
              disabled={advancing}
            />
            <Button
              variant="contained"
              data-testid="voice-quiz-submit"
              onClick={handleTypedSubmit}
              disabled={!typedAnswer.trim() || advancing}
            >
              Submit
            </Button>
          </Box>
        )}

        {(!micSupported || micDenied) && (
          <Alert severity="warning">Type your answer</Alert>
        )}

        {error && <Alert severity="info">{error}</Alert>}
      </Stack>

      <Button
        variant="contained"
        data-testid="voice-quiz-speak"
        aria-label="Speak answer"
        onClick={handleListen}
        disabled={!micSupported || micDenied || isListening || advancing}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          minWidth: 96,
          minHeight: 96,
          borderRadius: '50%',
          zIndex: 2,
        }}
      >
        {isListening ? <GraphicEq /> : <Mic />}
      </Button>
    </Paper>
  );
};

export default VoiceQuizRunner;
